import React, { useState } from "react";

import { useGetAllDashboardsInfo, useGetDashboard, useUpdateDashboard } from "api/api-hooks/dashboards-hooks";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { easasSuccessAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { APIError } from "services/HttpService";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { v4 as uuidv4 } from "uuid";
import { dashboardUpdateErrorHandler } from "../../utils";

interface MoveGroupOrComponentProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  component?: IDashboardComponent;
  group?: DashboardComponentGroup;
  tabId: number;
  groupId: string;
  componentId?: string;
  dashboardId: number;
  isComponent: boolean;
}

const MoveGroupOrComponent = (props: MoveGroupOrComponentProps) => {
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedDashboardTabId, setSelectedDashboardTabId] = useState<number | null>(
    props.isComponent ? props.tabId : null
  );
  const [selectedDashboard, setSelectedDashboard] = useState<number | null>(props.dashboardId);
  const currentDashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const dashboard = useDashboardStore(state => state.dashboard);

  const { data: allDashboardsInfo } = useGetAllDashboardsInfo();
  const { data: selectedDashboardInfo } = useGetDashboard(selectedDashboard || 0, {
    enabled: selectedDashboard !== null,
  });

  const updateQuery = useUpdateDashboard({
    onSuccess: data => {
      easasSuccessAlert(T("alert.dashboard-updated"));
      if (currentDashboard?.id === data.id) {
        setDashboard(data);
      }
    },
    onFailure: (error: APIError) => {
      dashboardUpdateErrorHandler(error, updateQuery.mutate, dashboard!);
    },
  });

  const currentJSDashboards = allDashboardsInfo?.filter(d => d.implementation === "JS");

  const tabOptions =
    selectedDashboardInfo?.dashboardTabsJs.map((tab, index) => ({
      label: tab.title,
      value: index,
    })) || [];

  const groupOptions = selectedDashboardInfo?.dashboardTabsJs.find(
    (tab, index) => index === selectedDashboardTabId
  )?.componentGroups;

  const handleTabChange = (e: DropdownChangeEvent) => {
    setSelectedDashboardTabId(e.value);
  };

  const updateGroupComponents = (
    groupId: string,
    components: IDashboardComponent[],
    dashboardInfo: Dashboard,
    selectedTabId: number
  ) => {
    return dashboardInfo?.dashboardTabsJs[selectedTabId]?.componentGroups.map(group => {
      if (group.id === groupId) {
        return { ...group, components };
      }
      return group;
    });
  };

  const removeComponentOrGroupFromSource = () => {
    if (!currentDashboard) return [];

    return currentDashboard.dashboardTabsJs.map((tab, index) => {
      if (index !== props.tabId) return tab;

      const updatedGroups = tab.componentGroups
        .map(group => {
          const isTargetGroup = group.id === props.groupId;

          if (!isTargetGroup) return group;

          if (props.isComponent) {
            const remainingComponents = group.components.filter(c => c.id !== props.componentId);

            return remainingComponents.length > 0 ? { ...group, components: remainingComponents } : null;
          }

          return null;
        })
        .filter((group): group is DashboardComponentGroup => group !== null);

      return {
        ...tab,
        componentGroups: updatedGroups,
      };
    });
  };

  const addComponentOrGroupToTarget = (dashboardInfo: Dashboard, tabId: number) => {
    if (selectedDashboardTabId !== null && props.component) {
      const componentToMove: IDashboardComponent = {
        ...props.component,
        id: uuidv4(),
      };

      const updatedComponents = [
        ...(selectedDashboardInfo?.dashboardTabsJs[selectedDashboardTabId]?.componentGroups?.find(
          group => group.id === selectedGroup
        )?.components || []),
        componentToMove,
      ];

      return updateGroupComponents(selectedGroup, updatedComponents, dashboardInfo, tabId);
    }
    return null;
  };

  const updateSourceDashboard = () => {
    const componentToMove: IDashboardComponent | null =
      props.isComponent && props.component ? { ...props.component, id: uuidv4() } : null;

    const removeFromSource = (currentTab: DashboardTabModel) => {
      return {
        ...currentTab,
        componentGroups: props.isComponent
          ? currentTab.componentGroups.map(group => {
              if (group.id === props.groupId) {
                return {
                  ...group,
                  components: group.components.filter(component => component.id !== props.componentId),
                };
              }
              return group;
            })
          : currentTab.componentGroups.filter(group => group.id !== props.groupId),
      };
    };

    const addToTarget = (currentTab: DashboardTabModel): DashboardTabModel => {
      if (props.isComponent && componentToMove) {
        const updatedGroups = currentTab.componentGroups.map(group => {
          if (group.id === selectedGroup) {
            return {
              ...group,
              components: [...group.components, componentToMove],
            };
          }
          return group;
        });

        return {
          ...currentTab,
          componentGroups: updatedGroups,
        };
      }

      if (props.group) {
        const newGroup: DashboardComponentGroup = {
          id: uuidv4(),
          title: props.group.title ?? "",
          components: props.group.components ?? [],
          layout: props.group.layout ?? {
            type: DashboardLayoutType.HORIZONTAL_FLEX,
          },
          reportOptions: props.group.reportOptions,
        };

        return {
          ...currentTab,
          componentGroups: [...currentTab.componentGroups, newGroup],
        };
      }

      return currentTab;
    };

    return (
      currentDashboard?.dashboardTabsJs.map((tab, index) => {
        let updatedTab = tab;

        if (index === props.tabId) {
          updatedTab = removeFromSource(updatedTab);
        }

        if (index === selectedDashboardTabId) {
          updatedTab = addToTarget(updatedTab);
        }
        return updatedTab as DashboardTabModel;
      }) ?? []
    );
  };

  const handleMoveToGroupOrTab = () => {
    if (
      selectedDashboard &&
      ((props.isComponent && selectedGroup) || !props.isComponent) &&
      selectedDashboardTabId !== null &&
      currentDashboard?.id !== undefined
    ) {
      const isSameDashboard = selectedDashboardInfo?.id === currentDashboard?.id;

      const updatedSourceDashboard: Dashboard = {
        ...currentDashboard,
        dashboardTabsJs: isSameDashboard ? updateSourceDashboard() : removeComponentOrGroupFromSource(),
      };
      updateQuery.mutate(updatedSourceDashboard);
      setDashboard(updatedSourceDashboard);

      if (!isSameDashboard && selectedDashboardInfo?.id !== undefined) {
        if (props.isComponent) {
          const updatedTargetDashboard = {
            ...selectedDashboardInfo,

            dashboardTabsJs: selectedDashboardInfo?.dashboardTabsJs.map((tab, index) => {
              if (index === selectedDashboardTabId) {
                return {
                  ...tab,
                  componentGroups:
                    addComponentOrGroupToTarget(selectedDashboardInfo, selectedDashboardTabId) ?? [],
                };
              }
              return tab;
            }),
          };

          updateQuery.mutate(updatedTargetDashboard);
        } else {
          const updatedTargetDashboard = {
            ...selectedDashboardInfo,
            dashboardTabsJs: selectedDashboardInfo?.dashboardTabsJs.map((tab, index) => {
              if (index === selectedDashboardTabId) {
                return {
                  ...tab,
                  componentGroups: [
                    ...tab.componentGroups,
                    {
                      ...props.group,
                      id: uuidv4(),
                      title: props.group?.title ?? "Untitled Group",
                      components: props.group?.components ?? [],
                      layout: props.group?.layout ?? { type: DashboardLayoutType.VERTICAL },
                    },
                  ],
                };
              }
              return tab;
            }),
          };
          updateQuery.mutate(updatedTargetDashboard);
        }
      }

      props.setIsVisible(false);
    }
  };

  return (
    <Dialog
      header={props.isComponent ? T("action.move-component") : T("action.move-group")}
      visible={props.isVisible}
      onHide={() => props.setIsVisible(false)}
      draggable={false}
      modal={false}
      blockScroll
      footer={
        <div>
          <Button
            label={T("word.cancel")}
            icon="pi pi-times"
            className="component-editor-button icon-red"
            onClick={() => props.setIsVisible(false)}
          />
          <Button
            label={T("action.move")}
            icon="pi pi-check"
            className="component-editor-button icon-green"
            onClick={handleMoveToGroupOrTab}
            disabled={props.isComponent ? !selectedGroup : selectedDashboardTabId === null}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-2">
          <label htmlFor="dashboard">{T("action.select-dashboard")}</label>
          <Dropdown
            id="dashboard"
            value={selectedDashboard}
            options={currentJSDashboards}
            filter
            onChange={e => setSelectedDashboard(e.value)}
            optionLabel="title"
            optionValue="id"
            placeholder={T("action.select-dashboard")}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="tab">{T("action.select-tab")}</label>
          <Dropdown
            id="tab"
            value={selectedDashboardTabId}
            options={tabOptions}
            filter
            onChange={handleTabChange}
            optionLabel="label"
            optionValue="value"
            placeholder={T("action.select-tab")}
            disabled={tabOptions.length === 0}
          />
        </div>
        {props.isComponent && (
          <div className="flex flex-column gap-2">
            <label htmlFor="currentGroups">{T("action.select-group")}:</label>
            <Dropdown
              id="currentGroups"
              value={selectedGroup}
              options={groupOptions?.map((group, index) => ({
                label: group.title || `${T("word.group")} ${index + 1}`,
                value: group.id,
              }))}
              onChange={e => setSelectedGroup(e.value)}
              placeholder={T("action.select-group")}
              disabled={selectedDashboard === null}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default MoveGroupOrComponent;
